/**
 * AI config per workflow: API keys per provider, custom models, workflow assignments.
 * Runtime uses getAIConfigForWorkflow(workflowId) and falls back to legacy getAIConfig() when no assignment exists.
 */

import { getDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from './firebase';
import { getAIConfig, type AIConfig, type AIProvider } from './cms';

export type AIWorkflowId =
  | 'blog_writing'
  | 'cms_page_coding'
  | 'article_context_suggestion'
  | 'planning';

export const AI_WORKFLOW_IDS: AIWorkflowId[] = [
  'blog_writing',
  'cms_page_coding',
  'article_context_suggestion',
  'planning',
];

export interface AIApiKeys {
  openai?: string;
  anthropic?: string;
  gemini?: string;
}

export interface AICustomModels {
  openai?: string[];
  anthropic?: string[];
  gemini?: string[];
}

export interface AIWorkflowAssignment {
  provider: AIProvider;
  model: string;
}

export interface AIWorkflowAssignments {
  blog_writing?: AIWorkflowAssignment;
  cms_page_coding?: AIWorkflowAssignment;
  article_context_suggestion?: AIWorkflowAssignment;
  planning?: AIWorkflowAssignment;
}

const settingsRef = (docId: string) => doc(db, 'artifacts', appId, 'settings', docId);

const EMPTY_KEYS: AIApiKeys = {};
const EMPTY_CUSTOM: AICustomModels = {};
const EMPTY_ASSIGNMENTS: AIWorkflowAssignments = {};

/**
 * Read API keys per provider from Firestore (settings/aiApiKeys).
 */
export async function getAIApiKeys(): Promise<AIApiKeys> {
  const snap = await getDoc(settingsRef('aiApiKeys'));
  if (!snap.exists()) return { ...EMPTY_KEYS };
  const data = snap.data();
  return {
    openai: data.openai ?? '',
    anthropic: data.anthropic ?? '',
    gemini: data.gemini ?? '',
  };
}

/**
 * Save API keys per provider to Firestore.
 */
export async function saveAIApiKeys(keys: AIApiKeys): Promise<void> {
  await setDoc(settingsRef('aiApiKeys'), {
    openai: keys.openai ?? '',
    anthropic: keys.anthropic ?? '',
    gemini: keys.gemini ?? '',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Read custom model IDs per provider from Firestore (settings/aiCustomModels).
 */
export async function getAICustomModels(): Promise<AICustomModels> {
  const snap = await getDoc(settingsRef('aiCustomModels'));
  if (!snap.exists()) return { ...EMPTY_CUSTOM };
  const data = snap.data();
  return {
    openai: Array.isArray(data.openai) ? data.openai : [],
    anthropic: Array.isArray(data.anthropic) ? data.anthropic : [],
    gemini: Array.isArray(data.gemini) ? data.gemini : [],
  };
}

/**
 * Save custom model IDs per provider to Firestore.
 */
export async function saveAICustomModels(custom: AICustomModels): Promise<void> {
  await setDoc(settingsRef('aiCustomModels'), {
    openai: custom.openai ?? [],
    anthropic: custom.anthropic ?? [],
    gemini: custom.gemini ?? [],
    updatedAt: serverTimestamp(),
  });
}

/**
 * Read workflow assignments from Firestore (settings/aiWorkflowAssignments).
 */
export async function getAIWorkflowAssignments(): Promise<AIWorkflowAssignments> {
  const snap = await getDoc(settingsRef('aiWorkflowAssignments'));
  if (!snap.exists()) return { ...EMPTY_ASSIGNMENTS };
  const data = snap.data();
  const map: AIWorkflowAssignments = {};
  for (const wid of AI_WORKFLOW_IDS) {
    const a = data[wid];
    if (a && typeof a.provider === 'string' && typeof a.model === 'string') {
      map[wid] = { provider: a.provider as AIProvider, model: a.model };
    }
  }
  return map;
}

/**
 * Save workflow assignments to Firestore.
 */
export async function saveAIWorkflowAssignments(
  assignments: AIWorkflowAssignments
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const wid of AI_WORKFLOW_IDS) {
    const a = assignments[wid];
    if (a) payload[wid] = { provider: a.provider, model: a.model };
  }
  await setDoc(settingsRef('aiWorkflowAssignments'), payload);
}

/**
 * Resolve AI config for a workflow: use assignment + apiKeys if present.
 * If assignment exists but key is only in legacy aiConfig, use workflow model with legacy key
 * so the admin-assigned model (e.g. GPT-4o Search for "Make me lucky") is respected.
 */
export async function getAIConfigForWorkflow(
  workflowId: AIWorkflowId
): Promise<AIConfig | null> {
  const [assignmentsSnap, keysSnap] = await Promise.all([
    getDoc(settingsRef('aiWorkflowAssignments')),
    getDoc(settingsRef('aiApiKeys')),
  ]);

  const assignment = assignmentsSnap.exists()
    ? (assignmentsSnap.data()[workflowId] as AIWorkflowAssignment | undefined)
    : undefined;

  if (assignment?.provider && assignment?.model) {
    const keys = keysSnap.exists() ? keysSnap.data() : {};
    let apiKey = (keys[assignment.provider] ?? '').trim();
    if (apiKey) {
      return {
        provider: assignment.provider,
        apiKey,
        model: assignment.model,
      };
    }
    // Assignment exists but key not in aiApiKeys — try legacy aiConfig and use workflow model with legacy key
    const legacy = await getAIConfig();
    if (legacy?.provider === assignment.provider && legacy?.apiKey?.trim()) {
      return {
        provider: assignment.provider,
        apiKey: legacy.apiKey.trim(),
        model: assignment.model,
      };
    }
  }

  return getAIConfig();
}
